import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, FileText, Plus, Trash2, Copy, CheckCircle
} from "lucide-react";
import { ChatToggleButton } from "@/components/AIChatWidget";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const ExpenseTemplates = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: "",
    description: "",
    property_type: "single_family",
    vacancy_rate: 5,
    property_taxes_percent: 1.2,
    insurance_percent: 0.5,
    maintenance_percent: 5,
    capex_percent: 5,
    management_percent: 10,
    hoa_monthly: 0,
    utilities_monthly: 0,
    other_monthly: 0
  });

  useEffect(() => {
    fetchTemplates();
  }, [token]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates/expenses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const createTemplate = async () => {
    if (!newTemplate.name) {
      toast.error("Please enter a template name");
      return;
    }

    try {
      await axios.post(`${API}/templates/expenses`, newTemplate, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Template created!");
      setShowCreateDialog(false);
      fetchTemplates();
      setNewTemplate({
        name: "",
        description: "",
        property_type: "single_family",
        vacancy_rate: 5,
        property_taxes_percent: 1.2,
        insurance_percent: 0.5,
        maintenance_percent: 5,
        capex_percent: 5,
        management_percent: 10,
        hoa_monthly: 0,
        utilities_monthly: 0,
        other_monthly: 0
      });
    } catch (error) {
      toast.error("Failed to create template");
    }
  };

  const deleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${API}/templates/expenses/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Template deleted");
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete template");
    }
  };

  const copyToClipboard = (template) => {
    const text = `Vacancy: ${template.expenses.vacancy_rate}%, Taxes: ${template.expenses.property_taxes_percent}%, Insurance: ${template.expenses.insurance_percent}%, Maintenance: ${template.expenses.maintenance_percent}%, CapEx: ${template.expenses.capex_percent}%, Management: ${template.expenses.management_percent}%`;
    navigator.clipboard.writeText(text);
    toast.success("Template values copied to clipboard!");
  };

  const formatPercent = (value) => `${value}%`;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900" data-testid="expense-templates-page">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <div className="h-6 w-px bg-slate-700"></div>
              <h1 className="text-lg font-semibold text-white font-['Manrope']">Expense Templates</h1>
            </div>
            <div className="flex items-center gap-3">
              <ChatToggleButton />
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                  data-testid="create-template-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Template
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-white">Create Expense Template</DialogTitle>
                  <DialogDescription className="text-slate-400">
                    Save your expense assumptions for quick reuse
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className="text-slate-300">Template Name *</Label>
                    <Input
                      value={newTemplate.name}
                      onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                      placeholder="My Custom Template"
                      className="bg-slate-950 border-slate-800 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Description</Label>
                    <Input
                      value={newTemplate.description}
                      onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                      placeholder="For properties in XYZ area"
                      className="bg-slate-950 border-slate-800 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Property Type</Label>
                    <Select 
                      value={newTemplate.property_type} 
                      onValueChange={(v) => setNewTemplate({ ...newTemplate, property_type: v })}
                    >
                      <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="single_family">Single Family</SelectItem>
                        <SelectItem value="duplex">Duplex</SelectItem>
                        <SelectItem value="triplex">Triplex</SelectItem>
                        <SelectItem value="fourplex">Fourplex</SelectItem>
                        <SelectItem value="multifamily">Multi-Family</SelectItem>
                        <SelectItem value="condo">Condo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Vacancy Rate (%)</Label>
                      <Input
                        type="number"
                        value={newTemplate.vacancy_rate}
                        onChange={(e) => setNewTemplate({ ...newTemplate, vacancy_rate: parseFloat(e.target.value) || 0 })}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Property Tax (%)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={newTemplate.property_taxes_percent}
                        onChange={(e) => setNewTemplate({ ...newTemplate, property_taxes_percent: parseFloat(e.target.value) || 0 })}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Insurance (%)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={newTemplate.insurance_percent}
                        onChange={(e) => setNewTemplate({ ...newTemplate, insurance_percent: parseFloat(e.target.value) || 0 })}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Maintenance (%)</Label>
                      <Input
                        type="number"
                        value={newTemplate.maintenance_percent}
                        onChange={(e) => setNewTemplate({ ...newTemplate, maintenance_percent: parseFloat(e.target.value) || 0 })}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">CapEx (%)</Label>
                      <Input
                        type="number"
                        value={newTemplate.capex_percent}
                        onChange={(e) => setNewTemplate({ ...newTemplate, capex_percent: parseFloat(e.target.value) || 0 })}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Management (%)</Label>
                      <Input
                        type="number"
                        value={newTemplate.management_percent}
                        onChange={(e) => setNewTemplate({ ...newTemplate, management_percent: parseFloat(e.target.value) || 0 })}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                  </div>
                  <Button 
                    onClick={createTemplate}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                  >
                    Create Template
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Info Banner */}
        <div className="glass-card rounded-xl p-4 mb-8 flex items-start gap-3">
          <FileText className="h-5 w-5 text-amber-500 mt-0.5" />
          <div>
            <h3 className="font-semibold text-white">What are Expense Templates?</h3>
            <p className="text-sm text-slate-400 mt-1">
              Expense templates save your common expense assumptions (vacancy, maintenance, etc.) 
              so you can quickly apply them to new property analyses. We've included 5 default templates 
              for common scenarios.
            </p>
          </div>
        </div>

        {/* Templates Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <div 
              key={template.id}
              className={`glass-card rounded-xl p-6 ${template.is_default ? 'border-l-4 border-cyan-500' : 'border-l-4 border-amber-500'}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-white">{template.name}</h3>
                    {template.is_default && (
                      <span className="text-xs px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-500">Default</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-400 mt-1">{template.description}</p>
                </div>
              </div>
              
              <div className="space-y-2 mb-4">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Vacancy</span>
                    <span className="font-mono text-slate-300">{formatPercent(template.expenses.vacancy_rate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Taxes</span>
                    <span className="font-mono text-slate-300">{formatPercent(template.expenses.property_taxes_percent)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Insurance</span>
                    <span className="font-mono text-slate-300">{formatPercent(template.expenses.insurance_percent)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Maintenance</span>
                    <span className="font-mono text-slate-300">{formatPercent(template.expenses.maintenance_percent)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">CapEx</span>
                    <span className="font-mono text-slate-300">{formatPercent(template.expenses.capex_percent)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Management</span>
                    <span className="font-mono text-slate-300">{formatPercent(template.expenses.management_percent)}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(template)}
                  className="flex-1 border-slate-700 text-slate-300 hover:text-white"
                >
                  <Copy className="h-3 w-3 mr-1" />
                  Copy
                </Button>
                {!template.is_default && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteTemplate(template.id)}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Quick Reference */}
        <div className="mt-12">
          <h2 className="text-xl font-semibold text-white mb-4">Quick Reference Guide</h2>
          <div className="glass-card rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase">Expense</th>
                  <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase">Conservative</th>
                  <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase">Moderate</th>
                  <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase">Aggressive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-800/50">
                  <td className="py-3 px-4 text-slate-300">Vacancy</td>
                  <td className="py-3 px-4 font-mono text-red-400">8-10%</td>
                  <td className="py-3 px-4 font-mono text-amber-400">5-7%</td>
                  <td className="py-3 px-4 font-mono text-emerald-400">3-5%</td>
                </tr>
                <tr className="border-b border-slate-800/50">
                  <td className="py-3 px-4 text-slate-300">Maintenance</td>
                  <td className="py-3 px-4 font-mono text-red-400">8-10%</td>
                  <td className="py-3 px-4 font-mono text-amber-400">5-7%</td>
                  <td className="py-3 px-4 font-mono text-emerald-400">3-5%</td>
                </tr>
                <tr className="border-b border-slate-800/50">
                  <td className="py-3 px-4 text-slate-300">CapEx</td>
                  <td className="py-3 px-4 font-mono text-red-400">8-10%</td>
                  <td className="py-3 px-4 font-mono text-amber-400">5-7%</td>
                  <td className="py-3 px-4 font-mono text-emerald-400">3-5%</td>
                </tr>
                <tr className="border-b border-slate-800/50">
                  <td className="py-3 px-4 text-slate-300">Management</td>
                  <td className="py-3 px-4 font-mono text-slate-400">8-10%</td>
                  <td className="py-3 px-4 font-mono text-slate-400">8-10%</td>
                  <td className="py-3 px-4 font-mono text-slate-400">0% (self)</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-slate-300">Property Taxes</td>
                  <td className="py-3 px-4 font-mono text-slate-400" colSpan={3}>Varies by location (0.5% - 2.5% of value)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ExpenseTemplates;
